import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Cult Arc — late-game spiritual movement spinoff.
 *
 * 4 decisions about doctrine, fees, and IRS treatment. Outcome:
 * either a tax-exempt "spiritual movement" (huge cash flow + IRS heat -40)
 * or a federal raid (FBI heat +60).
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns "doctrine score" delta
}

const BEATS: Beat[] = [
  {
    text: 'YOUR BUSINESS MANAGER: "what\'s the doctrine? we need a name for the thing."',
    options: [
      { label: '"Wealth Consciousness." quasi-religious. tax-exempt eligible.', effect: () => { useLife.getState().bumpIrsHeat(-15); usePower.getState().bumpRep('crypto', 6); return 2; } },
      { label: '"The Order of the Green Candle." finance-coded. very online.', effect: () => { usePower.getState().bumpRep('wsb', 18); usePower.getState().bumpRep('press', -4); return 2; } },
      { label: '"Just My Discord, Actually."', effect: () => { useMarkets.getState().addCash(40_000); usePower.getState().bumpRep('press', -3); return 1; } },
    ],
  },
  {
    text: 'TIER STRUCTURE — how aggressively do you monetize members?',
    options: [
      { label: '"the inner circle" — \$5K/yr. quarterly retreats. kombucha included.', effect: () => { useMarkets.getState().addCash(250_000); useLife.getState().bumpIrsHeat(8); return 2; } },
      { label: '"all access" — \$50K. lifetime. you get to call me on saturdays.', effect: () => { useMarkets.getState().addCash(800_000); useLife.getState().bumpIrsHeat(20); useLife.getState().bumpFbiHeat(8); return 3; } },
      { label: 'free. it\'s a movement, not a business.', effect: () => { useMarkets.getState().addCash(120_000); usePower.getState().bumpRep('press', 5); return 1; } },
    ],
  },
  {
    text: 'A 60 MINUTES PRODUCER calls. wants to do a piece. how do you handle?',
    options: [
      { label: 'invite them in. give them total access.', effect: () => { usePower.getState().bumpRep('press', 8); useLife.getState().bumpFbiHeat(15); return 1; } },
      { label: 'send them a cease-and-desist via your lawyer', effect: () => { useMarkets.getState().addCash(-25_000); usePower.getState().bumpRep('press', -10); return 0; } },
      { label: 'release a 47-minute "documentary" of your own first', effect: () => { usePower.getState().bumpNotoriety(20); usePower.getState().bumpRep('crypto', 8); return 2; } },
    ],
  },
  {
    text: 'IRS opens an inquiry into your "religious institution." last move?',
    options: [
      { label: 'lobby a senator to call it religious persecution', effect: () => { useMarkets.getState().addCash(-150_000); useLife.getState().bumpIrsHeat(-25); usePower.getState().bumpInfluence(10); return 3; } },
      { label: 'voluntarily restructure as a 501(c)(4) "issue advocacy" org', effect: () => { useMarkets.getState().addCash(-50_000); useLife.getState().bumpIrsHeat(-15); return 2; } },
      { label: 'flee to a country without an extradition treaty', effect: () => { useLife.getState().bumpFbiHeat(20); useLife.getState().bumpSanity(-8); return -1; } },
    ],
  },
];

let doctrineScore = 0;

export function startCultArc(): void {
  doctrineScore = 0;
  useMeta.getState().recordLore('arc-cult-founding', 'Cult Founding');
  useCinematic.getState().fire({
    id: `cult-${Date.now()}`,
    type: 'breaking',
    headline: 'YOU\'RE STARTING A MOVEMENT.',
    subhead: '"spiritual" or "financial" — depending on who\'s asking. 4 doctrine decisions. one IRS inquiry. all upside.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `cult-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `cult-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          doctrineScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `cult: doctrine ${i + 1}/4` });
      sounds.notifyAlert();
    }, 6_000 + i * 30_000);
  }

  setTimeout(() => {
    if (doctrineScore >= 7) {
      useCinematic.getState().fire({
        id: `cult-success-${Date.now()}`,
        type: 'ascension',
        headline: 'YOUR MOVEMENT IS TAX-EXEMPT.',
        subhead: 'congrats, you have founded a "spiritual community." 12,000 paying members. cash flow: significant. IRS: temporarily neutralized.',
        durationMs: 7000,
      });
      useMarkets.getState().addCash(2_000_000);
      useLife.getState().bumpIrsHeat(-40);
      usePower.getState().bumpInfluence(20);
      usePower.getState().bumpRep('crypto', 25);
      useXClone.getState().receivePost({
        authorHandle: 'WAGMI_VIP', authorTribe: 'crypto',
        text: 'i was lost. then i joined "the Order." now i own 4 NFTs of myself. life has direction. WAGMI.',
        likes: 14000, reposts: 4200,
      });
    } else {
      useCinematic.getState().fire({
        id: `cult-raid-${Date.now()}`,
        type: 'true-death',
        headline: 'FEDERAL RAID.',
        subhead: 'agents in tactical vests carry boxes labeled "doctrine" out of your office. the documentary will be released next year.',
        durationMs: 7500,
      });
      useLife.getState().bumpFbiHeat(60);
      useMarkets.getState().addCash(-500_000);
      useLife.getState().bumpSanity(-25);
      usePower.getState().bumpRep('press', -15);
      useXClone.getState().receivePost({
        authorHandle: 'WSJ_RealTimeBait', authorTribe: 'press',
        text: 'BREAKING: "the Order of the Green Candle" raided by federal agents. founder unreachable for comment. Netflix already optioned the documentary rights.',
        likes: 38000, reposts: 11200,
      });
    }
  }, 6_000 + BEATS.length * 30_000 + 5_000);
}
